import { useMutation } from '@do/seashell/graphql';
import { useNavigate } from '@do/seashell/navigation';
import { Form, Formik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import copy from '../copy';
import { resetPassword as ResetPasswordRequest, resetPasswordVariables } from '../generated-types/resetPassword';
import { RESET_PASSWORD } from '../queries/queries';
import { handleFormError } from '../utils/gql-utils';
import { yupPasswordValidation } from '../utils/validation-utils';
import ErrorBanner from './ErrorBanner';
import SpinnerButton from './SpinnerButton';
import TextInput from './TextInput';

const passwordResetValidation = Yup.object().shape({
  password: yupPasswordValidation,
});

interface Props {
  token: string;
}

const PasswordResetForm: React.FC<Props> = ({ token }) => {
  const [resetPassword, { loading: isLoading }] = useMutation<ResetPasswordRequest, resetPasswordVariables>(
    RESET_PASSWORD,
  );

  const [genericServerError, setGenericServerError] = React.useState(null);
  const navigate = useNavigate();

  return (
    <Formik
      initialValues={{
        password: '',
      }}
      validationSchema={passwordResetValidation}
      onSubmit={(values, { setErrors }) => {
        setGenericServerError(null); /* Reset displayed error */
        resetPassword({
          variables: {
            resetPasswordRequest: { password: values.password, token },
          },
        })
          .then(() => {
            navigate('/login', {
              state: { success: 'reset' },
            });
          })
          .catch(handleFormError(setErrors, setGenericServerError));
      }}
    >
      {({ errors, values, touched }) => (
        <Form>
          {genericServerError && <ErrorBanner error={genericServerError} />}

          <TextInput
            type="password"
            placeholder={copy.form.password_reset.label}
            name="password"
            required
            label={copy.form.password_reset.placeholder}
            touched={touched.password}
            error={errors.password}
            value={values.password}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
          <SpinnerButton
            type="submit"
            fullWidth
            spinning={isLoading}
            disabled={isLoading}
            loadingMessage={copy.form.password_reset.loading}
          >
            {copy.form.password_reset.button_text}
          </SpinnerButton>
        </Form>
      )}
    </Formik>
  );
};

export default PasswordResetForm;
